/**
 * Vital signs and device measurements on a chart.
 *
 * Replaces `PatientCpt.GetMeasurements`, `PatientCpt.GetMeasurements2` and
 * pacmny-be's `#requestVitalSignsCodes`. The `2` twin was the second generation
 * of the same table shipping beside the first.
 *
 * The write endpoint replaces one that was completely public — no token, no
 * session, gated on a literal string compared inside a form model — so anyone
 * who could reach the host could write glucose, blood-pressure, SpO2,
 * temperature and weight readings into any patient's chart.
 *
 * Readings that arrive **from a meter** are `devices.js`; this is the chart's
 * measurement history however it got there.
 */

import api from './client';

/**
 * Cursor-paginated, not offset-paginated, and that is deliberate: continuous
 * glucose is a 15-minute sample rate, and an offset page over a growing table
 * repeats and drops rows. Page with `meta.next_cursor`, never with `?page=`.
 * `?filter[code]`, `[device_id]`, `[source]`, `[provenance]` narrow it.
 */
export function listMeasurements(patientId, params = {}) {
  return api.list(`/patients/${patientId}/measurements`, params);
}

/** `{measured_at, entries: [{code, system, value, unit, note}]}`. */
export function recordMeasurements(patientId, payload) {
  return api.post(`/patients/${patientId}/measurements`, payload);
}

/**
 * The newest value per code — what a chart header shows. Declared ahead of the
 * collection route, so `latest` can never be read as a measurement id.
 */
export function getLatestMeasurements(patientId, params = {}) {
  return api.get(`/patients/${patientId}/measurements/latest`, params);
}

/**
 * The vital-sign vocabulary. The one read in the clinical context with no audit
 * type: it is identical for every caller in every organization and names no
 * patient, so logging it would bury the reads that matter.
 */
export function listVitalSignCodes(params = {}) {
  return api.get('/vital-sign-codes', params);
}

export default {
  listMeasurements, recordMeasurements, getLatestMeasurements, listVitalSignCodes,
};
